import { Injectable } from '@angular/core';

import { FormBuilder, FormGroup, Validators } from '@angular/forms';

import { FlockWeekregistrationKpiService } from './flock-weekregistration-kpi.service';
import { ProductionPeriodConfig } from '../components/shared/flock-variant.config';
import {
  InputMethodEnum,
  Flock,
  WeekRegistration,
  ValueRegistrationEnum,
  ValueRegistration,
} from '../models/flock.model';

@Injectable({
  providedIn: 'root',
})
export class WeekRegistrationService {
  previousWeek?: WeekRegistration;
  currentWeekRegistration: WeekRegistration = {
    weekNr: 0,
    dayRegistrations: [],
    startDay: null,
    endDay: null,
    registrationData: {},
  };
  isNewRegistration = true;
  formGroups: FormGroup[] = [];

  constructor(
    private kpiService: FlockWeekregistrationKpiService,
    private formBuilder: FormBuilder,
  ) {}

  getCurrentWeek(
    givenWeek: WeekRegistration,
    weekRegistrations: WeekRegistration[],
    flock: Flock,
    productionPeriod: ProductionPeriodConfig,
  ): WeekRegistration {
    this.isNewRegistration = true;

    // If there is no week found, we need to create a new one.
    if (givenWeek === undefined) {
      // determine first production week, which should start with production start date
      const productionStartDate = new Date(<string>flock.arrival.chickBirthDate);
      productionStartDate.setDate(
        productionStartDate.getDate() +
          productionPeriod.startInDays +
          flock.arrival.deviationOfBirthDateInDays,
      );
      const daysDifference = Math.round(
        Math.abs(
          new Date(<string>flock.arrival.arrivalDate).getTime() - productionStartDate.getTime(),
        ) /
          (1000 * 3600 * 24),
      );

      let weekNr = productionPeriod.startWeekNr - Math.floor(daysDifference / 7);

      if (givenWeek?.weekNr === undefined) {
        // if this is the first week that is entered, then return weeknr,
        // otherwise it should be the next weeknr.
        weekNr =
          weekRegistrations.length === 0
            ? weekNr
            : Math.max(...weekRegistrations.map((wr) => wr.weekNr)) + 1;
      }
      let daysModulo = 0;
      let startDay = 0;
      let endDay = 0;
      if (weekRegistrations.length === 0) {
        daysModulo = Math.floor(daysDifference % 7);
        // (this can only happen when the first week is being set)
        // if there are days left before the calculated weeknr, then a week before it must be set
        if (daysModulo > 0) {
          weekNr = Math.max(weekNr - 1, 1);
        }
        startDay =
          productionPeriod.startInDays + flock.arrival.deviationOfBirthDateInDays - daysDifference;

        // if there is a daysModulo > 0, then the first week will contain those days
        // otherwise it will be a full week of 7 days
        const endDayOffset = daysModulo > 0 ? daysModulo - 1 : 6;
        endDay =
          productionPeriod.fullFirstWeekFromArrival && daysModulo > 0
            ? startDay + 6
            : startDay + endDayOffset;
      } else {
        this.previousWeek = weekRegistrations.find((w) => w.weekNr === weekNr - 1);

        if (this.previousWeek !== undefined) {
          startDay = this.previousWeek.endDay + 1;
          endDay = startDay + 6;
        }

        // if the departuredate is set, then the endday is possibly now set beyond that and must be re-set
        if (flock.departure !== null) {
          const fromBirthToDeparture = Math.round(
            Math.abs(
              new Date(<string>flock.departure.departureDate).getTime() -
                new Date(<string>flock.arrival.chickBirthDate).getTime(),
            ) /
              (1000 * 3600 * 24),
          );
          // departuredate lies within this week, so the week should be the remaining days until that departuredate
          if (fromBirthToDeparture < endDay) {
            endDay = startDay + (6 - (endDay - fromBirthToDeparture));
          }
        }
      }

      // Create an empty new week registration instance.
      this.currentWeekRegistration = {
        weekNr,
        dayRegistrations: [],
        registrationData: {},
        startDay,
        endDay,
      };
    } else {
      this.isNewRegistration = false;
      this.currentWeekRegistration = {
        ...givenWeek,
      };
    }

    return this.currentWeekRegistration;
  }

  getInputMethod(flock: Flock): string {
    const inputDefinitionArray = <string[]>Object.values(flock.inputDefinition);
    const isAllSame =
      inputDefinitionArray.filter((v, _, a) => v === a[0]).length === inputDefinitionArray.length;

    return isAllSame ? inputDefinitionArray[0] : 'advanced';
  }

  createFormGroup(data: ValueRegistration, mode: InputMethodEnum, amountOfHens: number) {
    const eggWeightRanges = this.kpiService.getValueRegistrationRanges(
      ValueRegistrationEnum.EggWeight,
      mode,
      amountOfHens,
    );
    const eggsAmountRanges = this.kpiService.getValueRegistrationRanges(
      ValueRegistrationEnum.EggsAmount,
      mode,
      amountOfHens,
    );
    const eggsAmountSecondKindRanges = this.kpiService.getValueRegistrationRanges(
      ValueRegistrationEnum.EggsAmountSecondKind,
      mode,
      amountOfHens,
    );
    const forageRanges = this.kpiService.getValueRegistrationRanges(
      ValueRegistrationEnum.Forage,
      mode,
      amountOfHens,
    );
    const henWeightRanges = this.kpiService.getValueRegistrationRanges(
      ValueRegistrationEnum.HenWeight,
      mode,
      amountOfHens,
    );
    const mortalityRanges = this.kpiService.getValueRegistrationRanges(
      ValueRegistrationEnum.Mortality,
      mode,
      amountOfHens,
    );
    const waterRanges = this.kpiService.getValueRegistrationRanges(
      ValueRegistrationEnum.Water,
      mode,
      amountOfHens,
    );

    this.formGroups.push(
      this.formBuilder.group({
        eggWeight: [
          data?.eggWeight,
          [Validators.min(eggWeightRanges[0]), Validators.max(eggWeightRanges[1])],
        ],
        eggsAmount: [
          data?.eggsAmount,
          [Validators.min(eggsAmountRanges[0]), Validators.max(eggsAmountRanges[1])],
        ],
        eggsAmountSecondKind: [
          data?.eggsAmountSecondKind,
          [
            Validators.min(eggsAmountSecondKindRanges[0]),
            Validators.max(eggsAmountSecondKindRanges[1]),
          ],
        ],
        forage: [data?.forage, [Validators.min(forageRanges[0]), Validators.max(forageRanges[1])]],
        forageProduct: [data?.forageProduct, []],
        isSecondForage: [data?.secondForage !== null || data?.secondForageProduct !== null, []],
        secondForage: [
          data?.secondForage,
          [Validators.min(forageRanges[0]), Validators.max(forageRanges[1])],
        ],
        secondForageProduct: [data?.secondForageProduct, []],
        henWeight: [
          data?.henWeight,
          [Validators.min(henWeightRanges[0]), Validators.max(henWeightRanges[1])],
        ],
        isInMoult: [data?.isInMoult],
        mortality: [
          data?.mortality,
          [Validators.min(mortalityRanges[0]), Validators.max(mortalityRanges[1])],
        ],
        water: [data?.water, [Validators.min(waterRanges[0]), Validators.max(waterRanges[1])]],
        remarks: [data?.remarks],
      }),
    );

    return this.formGroups;
  }
}
